import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import {
  Users,
  HelpCircle,
  Bookmark,
  MessageSquare,
  Plus,
  Search,
  Filter,
  Check,
  X,
  ChevronDown,
  Sparkles,
  Briefcase,
  GraduationCap,
  Award,
  ArrowRight,
  UserCheck,
} from 'lucide-react'
import { colors, radii, typography } from '../../styles/tokens'
import { alumniApi, type AlumniProfileItem, type AlumniFilterParams } from '../../api/alumni'
import { qaApi, type QuestionSummary, QA_CATEGORIES, type QACategory } from '../../api/qa'
import { AlumniCard } from '../../components/alumni/AlumniCard'
import { QuestionCard } from '../../components/alumni/QuestionCard'
import { AskQuestionModal } from '../../components/alumni/AskQuestionModal'
import { EmptyState } from '../../components/shared/EmptyState'
import { useAuthStore } from '../../stores/authStore'
import toast from 'react-hot-toast'

type MainTab = 'discover' | 'questions' | 'my_questions' | 'saved' | 'alumni_desk'

export function AlumniScreen() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const currentUser = useAuthStore((s) => s.user)

  // Determine initial tab from search params or default to discover
  const initialTab = (searchParams.get('tab') as MainTab) || 'discover'
  const [activeTab, setActiveTab] = useState<MainTab>(initialTab)

  // Ask Question Modal state
  const [isAskModalOpen, setIsAskModalOpen] = useState(searchParams.get('ask') === '1')
  const [targetAlumnusForModal, setTargetAlumnusForModal] = useState<AlumniProfileItem | null>(null)

  // Check if current user is an alumnus
  const isUserAlumnus = currentUser?.account_type === 'alumni' || currentUser?.user_role === 'faculty'

  // ==========================================
  // TAB 1: DISCOVER ALUMNI STATE & EFFECTS
  // ==========================================
  const [alumniList, setAlumniList] = useState<AlumniProfileItem[]>([])
  const [alumniLoading, setAlumniLoading] = useState(false)
  const [alumniQuery, setAlumniQuery] = useState('')
  const [filterCollege, setFilterCollege] = useState('')
  const [filterCourse, setFilterCourse] = useState('')
  const [filterYear, setFilterYear] = useState('')
  const [filterCompany, setFilterCompany] = useState('')
  const [filterIndustry, setFilterIndustry] = useState('')
  const [filterSkill, setFilterSkill] = useState('')
  const [verifiedOnly, setVerifiedOnly] = useState(false)
  const [showFilterDrawer, setShowFilterDrawer] = useState(false)

  const fetchAlumni = useCallback(async () => {
    setAlumniLoading(true)
    try {
      const params: AlumniFilterParams = {
        limit: 50,
      }
      if (alumniQuery.trim()) params.q = alumniQuery.trim()
      if (filterCollege) params.college = filterCollege
      if (filterCourse) params.course = filterCourse
      if (filterYear) params.year = filterYear
      if (filterCompany) params.company = filterCompany
      if (filterIndustry) params.industry = filterIndustry
      if (filterSkill) params.skill = filterSkill
      if (verifiedOnly) params.verified_only = true

      const res = await alumniApi.getAll(params)
      setAlumniList(res.data.data || [])
    } catch {
      toast.error('Failed to load alumni profiles')
    } finally {
      setAlumniLoading(false)
    }
  }, [alumniQuery, filterCollege, filterCourse, filterYear, filterCompany, filterIndustry, filterSkill, verifiedOnly])

  useEffect(() => {
    if (activeTab === 'discover') {
      const timer = setTimeout(() => {
        fetchAlumni()
      }, 250)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [activeTab, fetchAlumni])

  // Extract unique filter options from loaded alumni or predefined list
  const uniqueColleges = useMemo(() => Array.from(new Set(alumniList.map((a) => a.previous_college).filter(Boolean))), [alumniList])
  const uniqueCompanies = useMemo(() => Array.from(new Set(alumniList.map((a) => a.company).filter(Boolean))), [alumniList])
  const uniqueIndustries = useMemo(() => Array.from(new Set(alumniList.map((a) => a.industry).filter(Boolean))), [alumniList])
  const uniqueYears = useMemo(() => Array.from(new Set(alumniList.map((a) => String(a.graduation_year)).filter(Boolean))).sort().reverse(), [alumniList])

  const hasActiveAlumniFilters = !!(filterCollege || filterCourse || filterYear || filterCompany || filterIndustry || filterSkill || verifiedOnly)

  const clearAlumniFilters = () => {
    setFilterCollege('')
    setFilterCourse('')
    setFilterYear('')
    setFilterCompany('')
    setFilterIndustry('')
    setFilterSkill('')
    setVerifiedOnly(false)
    setAlumniQuery('')
  }

  // ==========================================
  // TAB 2: QUESTIONS & ANSWERS STATE & EFFECTS
  // ==========================================
  const [questions, setQuestions] = useState<QuestionSummary[]>([])
  const [questionsLoading, setQuestionsLoading] = useState(false)
  const [questionsQuery, setQuestionsQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('All')
  const [sortOrder, setSortOrder] = useState<'newest' | 'most_answered' | 'unanswered'>('newest')

  const fetchQuestions = useCallback(async () => {
    setQuestionsLoading(true)
    try {
      const res = await qaApi.getQuestions({
        q: questionsQuery.trim() || undefined,
        category: selectedCategory !== 'All' ? selectedCategory : undefined,
        sort: sortOrder,
        limit: 40,
      })
      setQuestions(res.data.data || [])
    } catch {
      toast.error('Failed to load questions')
    } finally {
      setQuestionsLoading(false)
    }
  }, [questionsQuery, selectedCategory, sortOrder])

  useEffect(() => {
    if (activeTab === 'questions') {
      const timer = setTimeout(() => {
        fetchQuestions()
      }, 200)
      return () => clearTimeout(timer)
    }
    return undefined
  }, [activeTab, fetchQuestions])

  // ==========================================
  // TAB 3: MY QUESTIONS STATE & EFFECTS
  // ==========================================
  const [myQuestions, setMyQuestions] = useState<QuestionSummary[]>([])
  const [myQuestionsLoading, setMyQuestionsLoading] = useState(false)

  const fetchMyQuestions = useCallback(async () => {
    if (!currentUser?.id) return
    setMyQuestionsLoading(true)
    try {
      const res = await qaApi.getQuestions({
        user_id: 'me',
        sort: 'newest',
      })
      setMyQuestions(res.data.data || [])
    } catch {
      toast.error('Failed to load your questions')
    } finally {
      setMyQuestionsLoading(false)
    }
  }, [currentUser?.id])

  useEffect(() => {
    if (activeTab === 'my_questions') {
      fetchMyQuestions()
    }
  }, [activeTab, fetchMyQuestions])

  // ==========================================
  // TAB 4: SAVED ANSWERS & QUESTIONS STATE
  // ==========================================
  const [savedData, setSavedData] = useState<{ questions: QuestionSummary[]; answers: any[] }>({
    questions: [],
    answers: [],
  })
  const [savedLoading, setSavedLoading] = useState(false)
  const [savedFilter, setSavedFilter] = useState<'all' | 'question' | 'answer'>('all')

  const fetchSaved = useCallback(async () => {
    setSavedLoading(true)
    try {
      const res = await qaApi.getSaved(savedFilter)
      setSavedData(res.data.data || { questions: [], answers: [] })
    } catch {
      toast.error('Failed to load saved items')
    } finally {
      setSavedLoading(false)
    }
  }, [savedFilter])

  useEffect(() => {
    if (activeTab === 'saved') {
      fetchSaved()
    }
  }, [activeTab, fetchSaved])

  // ==========================================
  // ALUMNI DESK STATE (For Alumni mentors)
  // ==========================================
  const [deskFilter, setDeskFilter] = useState<'tagged' | 'expertise' | 'unanswered' | 'answered'>('tagged')
  const [deskQuestions, setDeskQuestions] = useState<QuestionSummary[]>([])
  const [deskLoading, setDeskLoading] = useState(false)

  const fetchDeskQuestions = useCallback(async () => {
    if (!currentUser?.id) return
    setDeskLoading(true)
    try {
      let params: any = {}
      if (deskFilter === 'tagged') {
        params = { alumnus_id: currentUser.id }
      } else if (deskFilter === 'unanswered') {
        params = { sort: 'unanswered' }
      } else if (deskFilter === 'answered') {
        params = { alumnus_id: currentUser.id, status: 'answered' }
      } else if (deskFilter === 'expertise') {
        params = { sort: 'newest' }
      }
      const res = await qaApi.getQuestions(params)
      setDeskQuestions(res.data.data || [])
    } catch {
      toast.error('Failed to load desk questions')
    } finally {
      setDeskLoading(false)
    }
  }, [currentUser?.id, deskFilter])

  useEffect(() => {
    if (activeTab === 'alumni_desk') {
      fetchDeskQuestions()
    }
  }, [activeTab, fetchDeskQuestions])

  // Handle Initial Alumnus Target from query param
  useEffect(() => {
    const alumnusParam = searchParams.get('alumnus')
    if (alumnusParam) {
      alumniApi
        .getById(alumnusParam)
        .then((res) => {
          setTargetAlumnusForModal(res.data.data)
          setIsAskModalOpen(true)
        })
        .catch(() => {})
    }
  }, [searchParams])

  const handleTabChange = (t: MainTab) => {
    setActiveTab(t)
    setSearchParams({ tab: t })
  }

  const handleOpenAskForAlumnus = (alm: AlumniProfileItem) => {
    setTargetAlumnusForModal(alm)
    setIsAskModalOpen(true)
  }

  return (
    <div
      style={{
        minHeight: '100%',
        background: 'var(--color-bg)',
        display: 'flex',
        flexDirection: 'column',
        paddingBottom: 'calc(var(--nav-height) + 32px)',
      }}
    >
      {/* Top Header Banner */}
      <div
        style={{
          padding: '24px 20px 20px',
          borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-surface)',
        }}
      >
        <div style={{ maxWidth: 1040, margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.6px',
                    padding: '2px 8px',
                    borderRadius: radii.pill,
                    background: '#111114',
                    color: '#fff',
                    fontFamily: typography.fontDisplay,
                  }}
                >
                  Verified Network
                </span>
                <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                  Campnect Professional Mentorship & Guidance
                </span>
              </div>
              <h1
                style={{
                  margin: '4px 0 6px',
                  fontSize: '26px',
                  fontWeight: 800,
                  letterSpacing: '-0.5px',
                  color: 'var(--color-text-primary)',
                  fontFamily: typography.fontDisplay,
                }}
              >
                Alumni Network & Student Q&A
              </h1>
              <p
                style={{
                  margin: 0,
                  fontSize: '14px',
                  color: 'var(--color-text-secondary)',
                  maxWidth: 620,
                  lineHeight: 1.5,
                }}
              >
                Connect directly with verified graduates and experienced industry professionals. Ask important questions about placements, internships, career pivots, and higher education.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                onClick={() => {
                  setTargetAlumnusForModal(null)
                  setIsAskModalOpen(true)
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: radii.pill,
                  border: 'none',
                  background: '#111114',
                  color: '#FFFFFF',
                  fontSize: '13.5px',
                  fontWeight: 700,
                  fontFamily: typography.fontDisplay,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: 'var(--shadow-sm)',
                  transition: 'transform 140ms ease',
                }}
                onPointerDown={(e) => (e.currentTarget.style.transform = 'scale(0.96)')}
                onPointerUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <Plus size={16} />
                Ask a Question
              </button>
            </div>
          </div>

          {/* 4 Navigation Tabs */}
          <div
            style={{
              display: 'flex',
              gap: '6px',
              marginTop: '22px',
              overflowX: 'auto',
              scrollbarWidth: 'none',
              paddingBottom: '2px',
            }}
          >
            {[
              { id: 'discover', label: 'Discover Alumni', icon: <Users size={15} /> },
              { id: 'questions', label: 'Questions & Answers', icon: <HelpCircle size={15} /> },
              { id: 'my_questions', label: 'My Questions', icon: <MessageSquare size={15} /> },
              { id: 'saved', label: 'Saved Answers', icon: <Bookmark size={15} /> },
              ...(isUserAlumnus
                ? [{ id: 'alumni_desk', label: 'Alumni Answering Desk', icon: <UserCheck size={15} /> }]
                : []),
            ].map((tabItem) => {
              const isActive = activeTab === tabItem.id
              return (
                <button
                  key={tabItem.id}
                  onClick={() => handleTabChange(tabItem.id as MainTab)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: radii.pill,
                    border: '1px solid',
                    borderColor: isActive ? '#111114' : 'var(--color-border)',
                    background: isActive ? '#111114' : 'var(--color-bg)',
                    color: isActive ? '#FFFFFF' : 'var(--color-text-secondary)',
                    fontSize: '13px',
                    fontWeight: isActive ? 700 : 600,
                    fontFamily: typography.fontDisplay,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '6px',
                    whiteSpace: 'nowrap',
                    transition: 'all 150ms ease',
                  }}
                >
                  {tabItem.icon}
                  {tabItem.label}
                  {tabItem.id === 'my_questions' && myQuestions.length > 0 && (
                    <span
                      style={{
                        fontSize: '10.5px',
                        fontWeight: 700,
                        padding: '1px 6px',
                        borderRadius: radii.pill,
                        background: isActive ? '#fff' : '#111114',
                        color: isActive ? '#111114' : '#fff',
                      }}
                    >
                      {myQuestions.length}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div style={{ maxWidth: 1040, margin: '0 auto', width: '100%', padding: '20px' }}>
        {/* ========================================================= */}
        {/* TAB 1: DISCOVER ALUMNI                                    */}
        {/* ========================================================= */}
        {activeTab === 'discover' && (
          <div>
            {/* Search and Filter Row */}
            <div
              style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '16px',
                flexWrap: 'wrap',
                alignItems: 'center',
              }}
            >
              <div style={{ position: 'relative', flex: 1, minWidth: 260 }}>
                <Search
                  size={16}
                  style={{ position: 'absolute', left: 14, top: 12, color: 'var(--color-text-muted)' }}
                />
                <input
                  type="text"
                  value={alumniQuery}
                  onChange={(e) => setAlumniQuery(e.target.value)}
                  placeholder="Search alumni by name, role, company, or skills..."
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 40px',
                    borderRadius: radii.pill,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '13.5px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {alumniQuery && (
                  <button
                    onClick={() => setAlumniQuery('')}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 10,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <button
                onClick={() => setShowFilterDrawer(!showFilterDrawer)}
                style={{
                  padding: '10px 18px',
                  borderRadius: radii.pill,
                  border: '1px solid',
                  borderColor: hasActiveAlumniFilters ? '#111114' : 'var(--color-border)',
                  background: hasActiveAlumniFilters ? '#111114' : 'var(--color-surface)',
                  color: hasActiveAlumniFilters ? '#fff' : 'var(--color-text-primary)',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Filter size={14} />
                Filters {hasActiveAlumniFilters && '●'}
              </button>

              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: 'var(--color-text-primary)',
                  cursor: 'pointer',
                  padding: '8px 12px',
                  borderRadius: radii.pill,
                  background: verifiedOnly ? 'var(--color-surface)' : 'transparent',
                  border: '1px solid',
                  borderColor: verifiedOnly ? '#111114' : 'var(--color-border)',
                }}
              >
                <input
                  type="checkbox"
                  checked={verifiedOnly}
                  onChange={(e) => setVerifiedOnly(e.target.checked)}
                  style={{ accentColor: '#111114' }}
                />
                Verified Alumni Only
              </label>

              {hasActiveAlumniFilters && (
                <button
                  onClick={clearAlumniFilters}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-secondary)',
                    fontSize: '12.5px',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    padding: '4px',
                  }}
                >
                  Clear all filters
                </button>
              )}
            </div>

            {/* Expandable Filter Drawer */}
            {showFilterDrawer && (
              <div
                style={{
                  padding: '16px',
                  borderRadius: 18,
                  background: 'var(--color-surface)',
                  border: '1px solid var(--color-border)',
                  marginBottom: '18px',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px',
                }}
              >
                <div>
                  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    College / University
                  </label>
                  <select
                    value={filterCollege}
                    onChange={(e) => setFilterCollege(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                    }}
                  >
                    <option value="">All Colleges</option>
                    {uniqueColleges.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Graduation Year
                  </label>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                    }}
                  >
                    <option value="">All Years</option>
                    {uniqueYears.map((y) => (
                      <option key={y} value={y}>
                        Class of {y}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Company / Organization
                  </label>
                  <select
                    value={filterCompany}
                    onChange={(e) => setFilterCompany(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                    }}
                  >
                    <option value="">All Companies</option>
                    {uniqueCompanies.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Industry
                  </label>
                  <select
                    value={filterIndustry}
                    onChange={(e) => setFilterIndustry(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                    }}
                  >
                    <option value="">All Industries</option>
                    {uniqueIndustries.map((ind) => (
                      <option key={ind} value={ind}>
                        {ind}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11.5px', fontWeight: 700, color: 'var(--color-text-muted)', marginBottom: '4px' }}>
                    Skill or Expertise
                  </label>
                  <input
                    type="text"
                    value={filterSkill}
                    onChange={(e) => setFilterSkill(e.target.value)}
                    placeholder="e.g. System Design, AI..."
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      borderRadius: radii.md,
                      border: '1px solid var(--color-border)',
                      background: 'var(--color-bg)',
                      color: 'var(--color-text-primary)',
                      fontSize: '12.5px',
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Alumni Cards Directory */}
            {alumniLoading ? (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '16px' }}>
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <div
                    key={n}
                    style={{
                      height: 220,
                      borderRadius: 20,
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                ))}
              </div>
            ) : alumniList.length > 0 ? (
              <div>
                <div style={{ fontSize: '13px', color: 'var(--color-text-secondary)', marginBottom: '14px' }}>
                  Showing <strong>{alumniList.length}</strong> verified alumni mentors
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '16px',
                  }}
                >
                  {alumniList.map((alumnus) => (
                    <AlumniCard
                      key={alumnus.id}
                      alumnus={alumnus}
                      onAskQuestion={handleOpenAskForAlumnus}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState
                icon={<Users size={36} color="var(--color-text-muted)" />}
                title="No alumni found"
                description="Try adjusting your search keywords or clearing applied filters."
                action={
                  <button
                    onClick={clearAlumniFilters}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Clear Filters
                  </button>
                }
              />
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 2: QUESTIONS & ANSWERS                                */}
        {/* ========================================================= */}
        {activeTab === 'questions' && (
          <div>
            {/* Search + Category Horizontal Scroll */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '20px' }}>
              <div style={{ position: 'relative' }}>
                <Search
                  size={16}
                  style={{ position: 'absolute', left: 14, top: 12, color: 'var(--color-text-muted)' }}
                />
                <input
                  type="text"
                  value={questionsQuery}
                  onChange={(e) => setQuestionsQuery(e.target.value)}
                  placeholder="Search questions by topic, keyword, or tag..."
                  style={{
                    width: '100%',
                    padding: '10px 14px 10px 40px',
                    borderRadius: radii.pill,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '13.5px',
                    outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
                {questionsQuery && (
                  <button
                    onClick={() => setQuestionsQuery('')}
                    style={{
                      position: 'absolute',
                      right: 12,
                      top: 10,
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'var(--color-text-muted)',
                    }}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              {/* Horizontal Category Pills */}
              <div
                style={{
                  display: 'flex',
                  gap: '6px',
                  overflowX: 'auto',
                  scrollbarWidth: 'none',
                  paddingBottom: '2px',
                }}
              >
                {['All', ...QA_CATEGORIES].map((cat) => {
                  const isCatActive = selectedCategory === cat
                  return (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      style={{
                        padding: '6px 14px',
                        borderRadius: radii.pill,
                        border: '1px solid',
                        borderColor: isCatActive ? '#111114' : 'var(--color-border)',
                        background: isCatActive ? '#111114' : 'var(--color-surface)',
                        color: isCatActive ? '#fff' : 'var(--color-text-secondary)',
                        fontSize: '12.5px',
                        fontWeight: isCatActive ? 700 : 500,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {cat}
                    </button>
                  )
                })}
              </div>

              {/* Sort Controls */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                    Sort by:
                  </span>
                  {[
                    { id: 'newest', label: 'Newest' },
                    { id: 'most_answered', label: 'Most Answered' },
                    { id: 'unanswered', label: 'Needs Answers' },
                  ].map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setSortOrder(s.id as any)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: radii.pill,
                        border: 'none',
                        background: sortOrder === s.id ? 'var(--color-surface)' : 'transparent',
                        color: sortOrder === s.id ? 'var(--color-text-primary)' : 'var(--color-text-muted)',
                        fontSize: '12px',
                        fontWeight: sortOrder === s.id ? 700 : 500,
                        cursor: 'pointer',
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setIsAskModalOpen(true)}
                  style={{
                    padding: '6px 14px',
                    borderRadius: radii.pill,
                    border: '1px solid var(--color-border)',
                    background: 'var(--color-surface)',
                    color: 'var(--color-text-primary)',
                    fontSize: '12.5px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                  }}
                >
                  <Plus size={13} /> Ask Question
                </button>
              </div>
            </div>

            {/* Questions Feed */}
            {questionsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    style={{
                      height: 160,
                      borderRadius: 20,
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      animation: 'pulse 1.5s infinite',
                    }}
                  />
                ))}
              </div>
            ) : questions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {questions.map((q) => (
                  <QuestionCard key={q.id} question={q} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<HelpCircle size={36} color="var(--color-text-muted)" />}
                title="No questions in this category yet"
                description="Be the first student to ask experienced alumni for guidance!"
                action={
                  <button
                    onClick={() => setIsAskModalOpen(true)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Ask a Question
                  </button>
                }
              />
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 3: MY QUESTIONS                                       */}
        {/* ========================================================= */}
        {activeTab === 'my_questions' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
                  Questions You Asked
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                  Track responses, follow-up answers, and advice from tagged alumni.
                </p>
              </div>
              <button
                onClick={() => setIsAskModalOpen(true)}
                style={{
                  padding: '8px 16px',
                  borderRadius: radii.pill,
                  border: 'none',
                  background: '#111114',
                  color: '#fff',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <Plus size={13} /> Ask Question
              </button>
            </div>

            {myQuestionsLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2].map((n) => (
                  <div key={n} style={{ height: 140, borderRadius: 20, background: 'var(--color-surface)' }} />
                ))}
              </div>
            ) : myQuestions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {myQuestions.map((q) => (
                  <QuestionCard key={q.id} question={q} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<MessageSquare size={36} color="var(--color-text-muted)" />}
                title="You haven't asked any questions yet"
                description="Ask alumni mentors about placements, interview roadmaps, internships, or grad school."
                action={
                  <button
                    onClick={() => setIsAskModalOpen(true)}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Ask Your First Question
                  </button>
                }
              />
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 4: SAVED ANSWERS & QUESTIONS                          */}
        {/* ========================================================= */}
        {activeTab === 'saved' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: 'var(--color-text-primary)' }}>
                  Saved Questions & Answers
                </h2>
                <p style={{ margin: '2px 0 0', fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                  Quickly reference career roadmaps and alumni answers you bookmarked.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '6px' }}>
                {[
                  { id: 'all', label: 'All Saved' },
                  { id: 'question', label: 'Questions' },
                  { id: 'answer', label: 'Answers' },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setSavedFilter(f.id as any)}
                    style={{
                      padding: '5px 12px',
                      borderRadius: radii.pill,
                      border: '1px solid',
                      borderColor: savedFilter === f.id ? '#111114' : 'var(--color-border)',
                      background: savedFilter === f.id ? '#111114' : 'transparent',
                      color: savedFilter === f.id ? '#fff' : 'var(--color-text-secondary)',
                      fontSize: '12px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {savedLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2].map((n) => (
                  <div key={n} style={{ height: 140, borderRadius: 20, background: 'var(--color-surface)' }} />
                ))}
              </div>
            ) : savedData.questions.length === 0 && savedData.answers.length === 0 ? (
              <EmptyState
                icon={<Bookmark size={36} color="var(--color-text-muted)" />}
                title="No saved items yet"
                description="Bookmark questions and answers in the Q&A feed to easily review them later."
                action={
                  <button
                    onClick={() => handleTabChange('questions')}
                    style={{
                      padding: '8px 18px',
                      borderRadius: radii.pill,
                      border: 'none',
                      background: '#111114',
                      color: '#fff',
                      fontSize: 13,
                      fontWeight: 700,
                      cursor: 'pointer',
                    }}
                  >
                    Explore Q&A
                  </button>
                }
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Saved Questions */}
                {savedData.questions.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Saved Questions ({savedData.questions.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {savedData.questions.map((q) => (
                        <QuestionCard key={q.id} question={q} onToggleSave={() => fetchSaved()} />
                      ))}
                    </div>
                  </div>
                )}

                {/* Saved Answers */}
                {savedData.answers.length > 0 && (
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: 700, margin: '14px 0 10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                      Saved Answers ({savedData.answers.length})
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {savedData.answers.map((ans) => (
                        <div
                          key={ans.id}
                          onClick={() => navigate(`/app/alumni/questions/${ans.question_id}`)}
                          style={{
                            background: 'var(--color-surface)',
                            border: '1px solid var(--color-border)',
                            borderRadius: 18,
                            padding: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                          }}
                        >
                          <div style={{ fontSize: '12.5px', color: 'var(--color-text-muted)' }}>
                            In discussion: <strong style={{ color: 'var(--color-text-primary)' }}>{ans.question_title}</strong>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                              Answer by {ans.author_name}
                            </span>
                            <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
                              ({ans.author_title} · {ans.author_company})
                            </span>
                          </div>
                          <p style={{ margin: 0, fontSize: '13.5px', color: 'var(--color-text-secondary)', lineHeight: 1.55 }}>
                            {ans.content}
                          </p>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', fontSize: '12px', color: 'var(--color-text-primary)', fontWeight: 600 }}>
                            View full discussion <ArrowRight size={13} style={{ marginLeft: 4 }} />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 5: ALUMNI ANSWERING DESK (For Mentors)                */}
        {/* ========================================================= */}
        {activeTab === 'alumni_desk' && (
          <div>
            <div
              style={{
                padding: '18px 20px',
                borderRadius: 20,
                background: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                marginBottom: '18px',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <Award size={16} style={{ color: '#111114' }} />
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 800 }}>
                  Alumni Mentorship Desk
                </h3>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: 'var(--color-text-secondary)' }}>
                Review questions directed to your expertise, provide guidance to junior students, and respond to follow-ups.
              </p>

              <div style={{ display: 'flex', gap: '6px', marginTop: '14px', flexWrap: 'wrap' }}>
                {[
                  { id: 'tagged', label: 'Questions Tagged In' },
                  { id: 'expertise', label: 'Matching Your Field' },
                  { id: 'unanswered', label: 'Unanswered Questions' },
                  { id: 'answered', label: 'Questions You Answered' },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setDeskFilter(f.id as any)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: radii.pill,
                      border: '1px solid',
                      borderColor: deskFilter === f.id ? '#111114' : 'var(--color-border)',
                      background: deskFilter === f.id ? '#111114' : 'var(--color-bg)',
                      color: deskFilter === f.id ? '#fff' : 'var(--color-text-secondary)',
                      fontSize: '12.5px',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {deskLoading ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {[1, 2].map((n) => (
                  <div key={n} style={{ height: 140, borderRadius: 20, background: 'var(--color-surface)' }} />
                ))}
              </div>
            ) : deskQuestions.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {deskQuestions.map((q) => (
                  <QuestionCard key={q.id} question={q} />
                ))}
              </div>
            ) : (
              <EmptyState
                icon={<UserCheck size={36} color="var(--color-text-muted)" />}
                title="No questions in this view"
                description="Check back soon for new student inquiries directed to your expertise."
              />
            )}
          </div>
        )}
      </div>

      {/* Ask Question Modal */}
      <AskQuestionModal
        isOpen={isAskModalOpen}
        onClose={() => {
          setIsAskModalOpen(false)
          setTargetAlumnusForModal(null)
        }}
        initialTargetAlumnus={targetAlumnusForModal}
        onSuccess={(newQId) => {
          fetchQuestions()
          fetchMyQuestions()
          navigate(`/app/alumni/questions/${newQId}`)
        }}
      />
    </div>
  )
}
